import {
  Component,
  computed,
  effect,
  inject,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormField, form, required } from '@angular/forms/signals';
import { ReservaApiService } from '@core/services/api/reserva-api.service';
import {
  IReservaRecursoData,
  IReservaData,
  IDisponibilidadData,
  ISlotDisponible,
} from '@core/interfaces/entidades/reserva.entidad';
import { ToastService } from '@shared/services/toast.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { AppService } from '@shared/services/app.service';
import { UiButton, UiSelect, UiDatepicker } from '@shared/components/ui';
import { FormsModule } from '@angular/forms';

interface HorarioBloque {
  hora_inicio: string;
  hora_fin: string;
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function rangesOverlap(aIni: string, aFin: string, bIni: string, bFin: string): boolean {
  return timeToMinutes(aIni) < timeToMinutes(bFin) && timeToMinutes(bIni) < timeToMinutes(aFin);
}

@Component({
  selector: 'app-crear-reserva',
  imports: [FormField, UiButton, UiSelect, FormsModule, UiDatepicker],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './crear-reserva.html',
})
export class CrearReserva {
  private api = inject(ReservaApiService);
  private toast = inject(ToastService);
  private sucursalService = inject(SucursalService);
  private appService = inject(AppService);

  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cargarRecursos();
      }
    });
  }

  readonly cerrar = output<IReservaData | undefined>();

  recursos = signal<IReservaRecursoData[]>([]);
  opcionesRecursos = computed(() => this.recursos().map((r) => ({ label: r.nombre, value: r.id })));
  recursoActivo = signal<IReservaRecursoData | null>(null);
  disponibilidad = signal<IDisponibilidadData | null>(null);
  fechaSeleccionada = signal<string>('');
  slotSeleccionado = signal<ISlotDisponible | null>(null);
  duracionMinutos = signal<number>(60);

  onDuracionChange(valor: number) {
    const v = Math.max(5, Math.min(480, valor || 60));
    this.duracionMinutos.set(v);
    this.slotSeleccionado.set(null);
    this.generarSlotsLocales();
  }

  slotsGenerados = signal<ISlotDisponible[]>([]);

  formReserva = form(
    signal({
      recurso_id: 0,
      fecha: '',
      hora_inicio: '',
      hora_fin: '',
      notas: '',
      notas_cliente: '',
      nombre_cliente_no_registrado: '',
      telefono_cliente_no_registrado: '',
      email_cliente_no_registrado: '',
    }),
    (schema) => {
      required(schema.recurso_id, { message: 'Seleccione un recurso' });
    },
  );

  cargarRecursos() {
    this.api.listarRecursos().subscribe({
      next: (data) => this.recursos.set(data.filter((r) => r.activo)),
      error: () =>
        this.toast.show('top-right', 'error', 'Error', 'No se pudieron cargar los recursos'),
    });
  }

  onRecursoChange(recursoId: number) {
    const recurso = this.recursos().find((r) => r.id === recursoId) ?? null;
    this.recursoActivo.set(recurso);
    this.duracionMinutos.set(recurso?.duracion_minutos ?? 60);
    this.formReserva().value.set({ ...this.formReserva().controlValue(), recurso_id: recursoId });
    this.slotSeleccionado.set(null);
    if (recursoId && this.fechaSeleccionada()) this.consultarDisponibilidad();
  }

  onFechaChange(fecha: Date) {
    const iso = fecha.toISOString().split('T')[0];
    this.fechaSeleccionada.set(iso);
    this.formReserva().value.set({ ...this.formReserva().controlValue(), fecha: iso });
    this.slotSeleccionado.set(null);
    if (this.formReserva().controlValue().recurso_id) this.consultarDisponibilidad();
  }

  consultarDisponibilidad() {
    const vals = this.formReserva().controlValue();
    if (!vals.recurso_id || !this.fechaSeleccionada()) return;
    this.api.consultarDisponibilidad(vals.recurso_id, this.fechaSeleccionada()).subscribe({
      next: (data) => {
        this.disponibilidad.set(data);
        this.generarSlotsLocales();
      },
      error: (err) => {
        this.disponibilidad.set(null);
        this.slotsGenerados.set([]);
        if (err.status !== 401)
          this.toast.show('top-right', 'error', 'Error', 'No se pudo consultar disponibilidad');
      },
    });
  }

  generarSlotsLocales() {
    const disp = this.disponibilidad();
    if (!disp) {
      this.slotsGenerados.set([]);
      return;
    }

    const duracion = this.duracionMinutos();
    const bloques: HorarioBloque[] = [
      ...disp.bloqueos.map((b) => ({ hora_inicio: b.hora_inicio, hora_fin: b.hora_fin })),
      ...disp.reservas_existentes.map((r) => ({
        hora_inicio: r.hora_inicio,
        hora_fin: r.hora_fin,
      })),
    ];

    const slots: ISlotDisponible[] = [];
    for (const h of disp.horario_laboral) {
      let cursor = timeToMinutes(h.hora_inicio);
      const fin = timeToMinutes(h.hora_fin);
      while (cursor + duracion <= fin) {
        const ini = minutesToTime(cursor);
        const fini = minutesToTime(cursor + duracion);
        const ocupado = bloques.some((b) => rangesOverlap(ini, fini, b.hora_inicio, b.hora_fin));
        slots.push({ hora_inicio: ini, hora_fin: fini, disponible: !ocupado });
        cursor += duracion;
      }
    }
    this.slotsGenerados.set(slots);
  }

  seleccionarSlot(slot: ISlotDisponible) {
    if (!slot.disponible) return;
    this.slotSeleccionado.set(slot);
    this.formReserva().value.set({
      ...this.formReserva().controlValue(),
      hora_inicio: slot.hora_inicio,
      hora_fin: slot.hora_fin,
    });
  }

  onCerrar(data?: IReservaData) {
    this.cerrar.emit(data);
  }

  guardar() {
    if (this.formReserva().invalid() || !this.slotSeleccionado()) {
      this.toast.show(
        'top-right',
        'warn',
        'Atención',
        'Seleccione un recurso, fecha, duración y horario disponible',
      );
      return;
    }
    const vals = this.formReserva().controlValue();
    this.api
      .crearReserva({
        recurso_id: vals.recurso_id,
        fecha: vals.fecha,
        hora_inicio: vals.hora_inicio,
        hora_fin: vals.hora_fin,
        notas: vals.notas || null,
        notas_cliente: vals.notas_cliente || null,
        nombre_cliente_no_registrado: vals.nombre_cliente_no_registrado || null,
        telefono_cliente_no_registrado: vals.telefono_cliente_no_registrado || null,
        email_cliente_no_registrado: vals.email_cliente_no_registrado || null,
      })
      .subscribe({
        next: (data) => {
          this.toast.show('top-right', 'success', 'Éxito', 'Reserva creada correctamente');
          this.onCerrar(data);
        },
        error: (err) => {
          if (err.status === 401) return;
          this.toast.show(
            'top-right',
            'error',
            'Error',
            err.error?.message ?? 'No se pudo crear la reserva',
          );
        },
      });
  }
}
